import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconEdit, IconFarm, IconPlus, IconTrash } from '../../../common/icons';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useFarmsList, useDeleteFarm } from '../../farms/contexts/farms';
import FarmForm from '../../farms/forms/farm-form';

import '../styles/detail.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

function farmAddress(location) {
    if (!location) return null;
    if (typeof location === 'string') return location;
    return location.address || null;
}

// Sección reutilizable de fincas de un cliente: ver + crear + editar + eliminar (FarmForm).
// Se usa en el detalle del cliente y en su formulario de edición. Las altas/bajas se
// aplican de inmediato (no dependen del "Guardar" del cliente). Gate farms:write.
export default function ClientFarmsPanel({ client }) {
    const clientId = client?.id;
    const { data, isLoading, error } = useFarmsList({ owner_id: clientId });
    const { mutateAsync: removeFarm, isPending: removingFarm } = useDeleteFarm();
    const [farmForm, setFarmForm] = useState(null); // null | 'new' | farm(edit)
    const confirm = useConfirm();

    const farms = data?.items ?? [];

    const delFarm = async (farm) => {
        const ok = await confirm({
            title: 'Eliminar finca',
            message: `¿Eliminar la finca "${farm.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await removeFarm(farm.id); toast.success('Finca eliminada'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar la finca'); }
    };

    return (
        <section className="client-detail__card">
            <div className="client-detail__section-head">
                <h2 className="client-detail__section-title">
                    Fincas
                    {!isLoading && <span className="client-detail__count">{farms.length}</span>}
                </h2>
                <Gate domain="farms" action="write">
                    <button type="button" className="client-detail__add-farm" onClick={() => setFarmForm('new')}>
                        <IconPlus /> <span>Nueva finca</span>
                    </button>
                </Gate>
            </div>

            {isLoading ? (
                <p className="client-detail__muted">Cargando fincas…</p>
            ) : error ? (
                <p className="client-detail__muted">No se pudieron cargar las fincas.</p>
            ) : !farms.length ? (
                <p className="client-detail__muted">Este cliente no tiene fincas registradas.</p>
            ) : (
                <ul className="client-detail__farms">
                    {farms.map((farm) => {
                        const address = farmAddress(farm.location);
                        return (
                            <li key={farm.id} className="farm-card">
                                <span className="farm-card__icon"><IconFarm /></span>
                                <div className="farm-card__body">
                                    <span className="farm-card__name">{farm.name}</span>
                                    <span className="farm-card__meta">
                                        {farm.size != null && <span>{farm.size} ha</span>}
                                        {address && <span>{address}</span>}
                                    </span>
                                </div>
                                <span className={`client-badge client-badge--${farm.status}`}>
                                    {STATUS_LABEL[farm.status] || '—'}
                                </span>
                                <Gate domain="farms" action="write">
                                    <span className="farm-card__tools">
                                        <button type="button" className="farm-card__tool" onClick={() => setFarmForm(farm)} disabled={removingFarm} aria-label="Editar finca" title="Editar"><IconEdit /></button>
                                        <button type="button" className="farm-card__tool farm-card__tool--danger" onClick={() => delFarm(farm)} disabled={removingFarm} aria-label="Eliminar finca" title="Eliminar"><IconTrash /></button>
                                    </span>
                                </Gate>
                            </li>
                        );
                    })}
                </ul>
            )}

            {farmForm && (
                <FarmForm
                    client={client}
                    farm={farmForm === 'new' ? null : farmForm}
                    onClose={() => setFarmForm(null)}
                    onSaved={() => setFarmForm(null)}
                />
            )}
        </section>
    );
}
